import { useEffect, useState } from 'react';
import { fetchMe, fetchProfileUrl } from '@/launch/api';

type Me = Awaited<ReturnType<typeof fetchMe>>;

export function RecordMyNameView({ token }: { token: string }): JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMe(token)
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleEditProfile(): Promise<void> {
    setBusy(true);
    setProfileError(null);
    try {
      const { profileUrl } = await fetchProfileUrl(token);
      window.open(profileUrl, '_blank', 'noopener');
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">Couldn't load profile</h1>
          <p className="mt-2 text-sm text-red-600 break-words">{error}</p>
        </div>
      </main>
    );
  }

  if (!me) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <p className="text-sm text-gray-600">Loading…</p>
      </main>
    );
  }

  const displayName = me.name ?? me.email ?? 'You';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xl font-semibold text-orange-700">
              {initials || '·'}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 truncate">{displayName}</h1>
              <p className="mt-1 text-sm text-gray-500">
                Record your name so others hear it pronounced correctly.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void handleEditProfile()}
              disabled={busy}
              className="inline-flex items-center justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 disabled:opacity-60"
            >
              {busy ? 'Opening…' : 'Record my name'}
            </button>
          </div>
          {profileError ? (
            <p className="mt-3 text-sm text-red-600 break-words">
              Profile link failed: {profileError}
            </p>
          ) : null}
        </section>
      </div>
    </main>
  );
}
